/**
 * Système de relations concurrentielles — Phase 1
 * ─────────────────────────────────────────────────
 * Chaque concurrent a un score de relation 0-100 (50 = neutre par défaut)
 * qui influence les futurs aléas narratifs (OPA, fusion, sous-traitance,
 * débauchage…) et conditionne la sympathie/hostilité avec le joueur.
 *
 * Phase 1 — Lecture/affichage du score, mapping vers un tier visuel.
 * Phases ultérieures :
 *   2) Modificateurs auto (challenges, embauche, M&A) + drift mensuel
 *   3) Événements OPA / fusion / plainte / débauchage avec choix narratifs
 *   4) Sous-traitance entrante/sortante & onglet Diplomatie
 *   5) Templates messages + tests Jest narratifs
 *
 * 17/06/2026 — Création.
 */

import { Handshake, Users, Minus, Swords, Skull } from 'lucide-react';

/**
 * Borne le score dans [0, 100].
 */
export function clampRelationScore(score) {
  if (!Number.isFinite(score)) return 50;
  if (score < 0) return 0;
  if (score > 100) return 100;
  return Math.round(score);
}

/**
 * Catalogue des modificateurs de relation (Phase 2).
 * Toutes les actions qui modifient une relation passent par ces clés pour
 * garantir une logique uniforme + faciliter l'analytics/historique.
 */
export const RELATION_DELTAS = {
  // Le joueur a remporté un appel d'offres contre ce concurrent.
  TENDER_WON_AGAINST: -5,
  // Le joueur a perdu un appel d'offres contre ce concurrent.
  TENDER_LOST_AGAINST: +2,
  // Le joueur a remporté un challenge (Marchetti, etc.) où il fallait
  // battre ce concurrent.
  CHALLENGE_WON_AGAINST: -8,
  // Embauche d'un chauffeur initialement chez ce concurrent (post-rachat).
  HIRED_DRIVER_FROM: -8,
  // Le joueur a surenchéri pour garder un chauffeur ciblé par ce
  // concurrent (rebute fortement le rival).
  POACH_DEFENDED: -12,
  // Acquisition / M&A de ce concurrent : relation absorbée, devient
  // neutre. (Le clan effet est géré séparément via M_AND_A_CLAN_EFFECT.)
  M_AND_A_TARGET: 0,
  // Effet "clan" : tout M&A par le joueur dégrade les relations avec les
  // concurrents NON acquis (perçu comme prédateur).
  M_AND_A_CLAN_EFFECT: -10,
  // Le joueur a signé un partenariat / alliance avec ce concurrent.
  ALLIANCE_SIGNED: +12,
  // Sous-traitance acceptée (le joueur sous-traite à ce concurrent).
  SUBCONTRACT_ACCEPTED_FROM: +5,
  // Sous-traitance donnée (le joueur prend des contrats de ce concurrent).
  SUBCONTRACT_GIVEN_TO: +5,
  // Le joueur a refusé une OPA hostile de ce concurrent.
  OPA_REFUSED: -20,
  // Drift mensuel vers la neutralité (déclenché mensuellement par
  // driftRelationsTowardNeutral).
  MONTHLY_DRIFT_STEP: 2,
};

/**
 * Applique un drift naturel des relations vers la neutralité (50).
 * Une relation 80 deviendra 78, une relation 20 deviendra 22.
 * Toutes les relations exactement à 50 restent inchangées.
 *
 * Pas applicable aux scores aux extrémités absolues (0 / 100) :
 * c'est volontaire — un "ennemi juré" ou un "allié indéfectible"
 * ne re-neutralise pas tout seul, il faut une action concrète.
 *
 * @param {Object} relations — { competitorId: score }
 * @param {number} [step=RELATION_DELTAS.MONTHLY_DRIFT_STEP] — pas du drift
 * @returns {Object} nouvelles relations
 */
export function driftRelationsTowardNeutral(relations, step = RELATION_DELTAS.MONTHLY_DRIFT_STEP) {
  const out = {};
  Object.entries(relations || {}).forEach(([id, raw]) => {
    const score = clampRelationScore(raw);
    if (score === 50 || score === 0 || score === 100) {
      out[id] = score;
      return;
    }
    if (score > 50) out[id] = clampRelationScore(score - step);
    else out[id] = clampRelationScore(score + step);
  });
  return out;
}

/**
 * Tier visuel du score de relation. Ordonné du plus hostile au plus ami,
 * chaque seuil borne strictement au tier supérieur.
 *
 *   0-15  → enemy   "Ennemi juré"      🔴
 *  16-35  → rival   "Rival"            🟠
 *  36-55  → neutral "Challenger / neutre" 🟡 (défaut à 50)
 *  56-75  → peer    "Collègue de profession" 🔵
 *  76-100 → ally    "Ami / Partenaire" 🟢
 */
export const RELATION_TIERS = [
  { id: 'enemy',   min: 0,  max: 15,  labelKey: 'relation_tier_enemy',   color: '#DC2626', icon: Skull,     emoji: '🔴' },
  { id: 'rival',   min: 16, max: 35,  labelKey: 'relation_tier_rival',   color: '#F97316', icon: Swords,    emoji: '🟠' },
  { id: 'neutral', min: 36, max: 55,  labelKey: 'relation_tier_neutral', color: '#EAB308', icon: Minus,     emoji: '🟡' },
  { id: 'peer',    min: 56, max: 75,  labelKey: 'relation_tier_peer',    color: '#3B82F6', icon: Users,     emoji: '🔵' },
  { id: 'ally',    min: 76, max: 100, labelKey: 'relation_tier_ally',    color: '#10B981', icon: Handshake, emoji: '🟢' },
];

/**
 * Renvoie le tier correspondant à un score.
 */
export function getRelationTier(score) {
  const s = clampRelationScore(score);
  return RELATION_TIERS.find(t => s >= t.min && s <= t.max) || RELATION_TIERS[2]; // fallback neutral
}

/**
 * Lecture du score de relation pour un concurrent donné dans le store.
 * Tolère un état `competitorRelations` absent (parties pré-Phase 1).
 *
 * @param {Object|null} relations — slice de state `competitorRelations` ({ id: score, ... })
 * @param {string} competitorId
 * @returns {number} score 0-100 (50 par défaut)
 */
export function readRelationScore(relations, competitorId) {
  if (!relations || !competitorId) return 50;
  const raw = relations[competitorId];
  return clampRelationScore(Number.isFinite(raw) ? raw : 50);
}

/**
 * Initialise un objet `competitorRelations` complet pour une liste de
 * concurrents (chacun à 50). Idempotent : appliqué via spread, ne
 * remplace pas les scores déjà persistés.
 */
export function defaultRelationsFor(competitors) {
  const out = {};
  (competitors || []).forEach(c => {
    if (c && c.id) out[c.id] = 50;
  });
  return out;
}

/**
 * Trouve l'id d'un concurrent à partir de son nom (utilisé pour mapper
 * les tenders dont la liste d'opposants est en chaînes "TransNord Express").
 * Renvoie null si non trouvé (concurrents "hors roster" type Géodis).
 *
 * @param {Array} competitors — liste COMPETITORS de competitors.js
 * @param {string} name
 * @returns {string|null}
 */
export function findCompetitorIdByName(competitors, name) {
  if (!name || !Array.isArray(competitors)) return null;
  const normalize = (s) => String(s || '').trim().toLowerCase();
  const target = normalize(name);
  const hit = competitors.find(c => normalize(c.name) === target);
  return hit ? hit.id : null;
}
